import { ContentfulImage } from '@ecomm/contentful/components'
import {
  V2GuidedSystemBuilder as GuidedSystemBuilder,
  type QuoteWizardType
} from '@ecomm/shared-components'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/function'
import { match } from 'ts-pattern'
import { literal } from 'zod'

import DuplexText from './DuplexText'
import type {
  DuplexImageLink,
  DuplexSmallText,
  PossibleEmbeddedComponent
} from './schema'

const getMappedComponent = (
  component: PossibleEmbeddedComponent,
  index: number
) =>
  match(component)
    .with({ __typename: literal('Text').value }, (data: DuplexSmallText) =>
      O.some(
        data.text?.json ? (
          <DuplexText key={index} text={data.text?.json} />
        ) : null
      )
    )
    .with(
      { __typename: literal('QuoteWizard').value },
      (data: QuoteWizardType['quoteWizard']) =>
        O.some(
          <div
            className="rounded-base bg-neutral-light-100 w-full px-4 py-6 md:p-8"
            key={`QuoteWizard-${index}`}
          >
            <GuidedSystemBuilder key={index} {...data} />
          </div>
        )
    )
    .with({ __typename: literal('ImageLink').value }, (data: DuplexImageLink) =>
      O.some(
        <ContentfulImage
          className="rounded-base col-span-2 hidden h-auto self-stretch first:block md:col-span-1 md:block lg:col-span-2 lg:h-fit"
          format="unset"
          key={`ImageLink-${index}`}
          quality={90}
          {...data.image}
        />
      )
    )
    .otherwise(() => O.none)

const mapDuplexComponents = (
  components: readonly PossibleEmbeddedComponent[]
) =>
  components.map((componentData, index) =>
    pipe(getMappedComponent(componentData, index), O.toNullable)
  )

type DuplexSectionProps = {
  readonly left: readonly PossibleEmbeddedComponent[]
  readonly right: readonly PossibleEmbeddedComponent[]
}

export function DuplexSection({ left, right }: DuplexSectionProps) {
  const quoteWizardMobile = right.filter(
    item => item.__typename === 'QuoteWizard'
  )

  const imageLink = right.filter(item => item.__typename === 'ImageLink')

  return (
    <div
      className="grid w-full grid-cols-2 items-start gap-4 md:gap-8"
      data-testid={DuplexSection.name}
    >
      <div className="col-span-2 md:order-2 lg:col-span-1">
        {mapDuplexComponents(quoteWizardMobile)}
      </div>
      <div className="col-span-2 grid grid-cols-2 md:order-1 md:gap-6 lg:col-span-1 lg:row-span-2 lg:gap-0">
        {mapDuplexComponents(left)}
      </div>
      <div className="col-span-2 grid grid-cols-2 gap-4 md:order-3 md:gap-6 lg:col-span-1">
        {mapDuplexComponents(imageLink)}
      </div>
    </div>
  )
}
